import axios from "axios";
import * as types from "../accountValueTypes";
import { bugsnagClient } from "~/plugins";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range";

// Default state
export const state = {
  profit_trades_last_week: [],
  recent_profits_per_base_currency: null,
  all_time_profits_per_base_currency: null,
  value: {
    Binance: {},
    Bittrex: {},
    Huobi: {}
  },
  managedValueHistory: {},
  managedValueUSD: [],
  enabledMarketCount: 0,
  accountValueHistory: {
    BTC: { Bittrex: {}, Binance: {}, Huobi: {} },
    USD: { Bittrex: {}, Binance: {}, Huobi: {} }
  }
};

// Getters: return computed state
export const getters = {
  values: state => {
    return state.value;
  },
  managed_value_usd: state => {
    return state.managedValueUSD;
  },
  managed_value_history: state => {
    return state.managedValueHistory;
  },
  account_value_history: state => {
    return state.accountValueHistory;
  },
  enabled_market_count: state => {
    return state.enabledMarketCount;
  },
  recent_profits_per_base_currency: state => {
    return state.recent_profits_per_base_currency;
  },
  all_time_profits_per_base_currency: state => {
    return state.all_time_profits_per_base_currency;
  },
  profit_trades_last_week: state => {
    if (!state.profit_trades_last_week) {
      return [];
    }

    let tradeData = {};
    for (let item of state.profit_trades_last_week) {
      let ts = moment
        .utc(String(item.ts_closed_utc))
        .tz(moment.tz.guess())
        .format("YYYY-MM-DD");

      if (!tradeData[ts]) {
        tradeData[ts] = { count: 0, usd: 0 };
      }

      tradeData[ts].count++;
      tradeData[ts].usd += item.profit_usd;
    }

    let dayData = {},
      str;

    for (let offset of Array(7).keys()) {
      str = moment()
        .subtract(offset, "day")
        .format("YYYY-MM-DD");

      if (!tradeData[str]) {
        dayData[str] = { count: 0, usd: 0 };
      } else {
        dayData[str] = tradeData[str];
      }
    }
    return dayData;
  },
  tabsNamesForProfits: state => {
    let data = {
      Binance: null,
      Bittrex: null,
      Huobi: null
    };
    if (!Array.isArray(state.recent_profits_per_base_currency)) {
      data = { ...data, ...state.recent_profits_per_base_currency };
    }
    if (!Array.isArray(state.all_time_profits_per_base_currency)) {
      data = { ...data, ...state.all_time_profits_per_base_currency };
    }
    if (
        (
            state.recent_profits_per_base_currency != null &&
            Array.isArray(state.recent_profits_per_base_currency) &&
            state.recent_profits_per_base_currency.length === 0)
        &&
        (
            state.all_time_profits_per_base_currency != null &&
            Array.isArray(state.all_time_profits_per_base_currency) &&
            state.all_time_profits_per_base_currency.length === 0
      )
    ) {
      return null;
    }
    return data;
  }
};

// Mutate the state
export const mutations = {
  [types.FETCH_ACCOUNT_VALUE_SUCCESS](state, { result }) {
    state.value.Binance = result.account_value.Binance
      ? result.account_value.Binance
      : {};
    state.value.Bittrex = result.account_value.Bittrex
      ? result.account_value.Bittrex
      : {};
    state.value.Huobi = result.account_value.Huobi
      ? result.account_value.Huobi
      : {};

    state.managedValueHistory = result.managed_value_history;
    state.accountValueHistory = result.account_value_history;
    state.enabledMarketCount = result.enabled_market_count;

    state.managedValueUSD = [];
    const alreadyProcessedExchanges = [];
    for (const [exchange, value] of Object.entries(result.managed_value_usd)) {
      if (alreadyProcessedExchanges.includes(exchange) === false) {
        state.managedValueUSD.push({ exchange, value });
        alreadyProcessedExchanges.push(exchange);
      }
    }
  },
  [types.FETCH_ACCOUNT_VALUE_FAILURE]() {},
  [types.FETCH_PROFIT_TRADES_LAST_WEEK_SUCCESS](
    state,
    { profit_trades_last_week }
  ) {
    state.profit_trades_last_week = profit_trades_last_week;
  },
  [types.FETCH_PROFIT_TRADES_LAST_WEEK_FAILURE](state) {
    state.profit_trades_last_week = [];
  },
  [types.FETCH_RECENT_PROFITS_SUCCESS](state, { profits_per_base_currency }) {
    state.recent_profits_per_base_currency = profits_per_base_currency;
  },
  [types.FETCH_RECENT_PROFITS_FAILURE](state) {
    state.recent_profits_per_base_currency = [];
  },
  [types.FETCH_ALL_TIME_PROFITS_SUCCESS](state, { profits_per_base_currency }) {
    state.all_time_profits_per_base_currency = profits_per_base_currency;
  },
  [types.FETCH_ALL_TIME_PROFITS_FAILURE](state) {
    state.all_time_profits_per_base_currency = [];
  }
};

// Perform async actions / api calls
export const actions = {
  async fetchAll({ dispatch }) {
    dispatch("fetchAccountValue");
    dispatch("fetchProfitableTradesLastWeek");
    dispatch("fetchRecentProfitsPerBaseCurrency");
    dispatch("fetchAllTimeProfitsPerBaseCurrency");
  },

  async fetchAccountValue({ commit }) {
    try {
      const { data } = await axios.get("/api/v1/user/account-value");

      commit(types.FETCH_ACCOUNT_VALUE_SUCCESS, { result: data.data });
    } catch (error) {
      commit(types.FETCH_ACCOUNT_VALUE_FAILURE);

      bugsnagClient.notify(error);
    }
  },
  async fetchProfitableTradesLastWeek({ commit }) {
    try {
      const { data } = await axios.get("/api/v1/values/trades-this-week");

      commit(types.FETCH_PROFIT_TRADES_LAST_WEEK_SUCCESS, {
        profit_trades_last_week: data
      });
    } catch (e) {
      commit(types.FETCH_PROFIT_TRADES_LAST_WEEK_FAILURE);
    }
  },
  async fetchRecentProfitsPerBaseCurrency({ commit }) {
    try {
      const { data } = await axios.get(
        "/api/v1/values/profits-per-base-currency/recent"
      );

      commit(types.FETCH_RECENT_PROFITS_SUCCESS, {
        profits_per_base_currency: data
      });
    } catch (e) {
      commit(types.FETCH_RECENT_PROFITS_FAILURE);
    }
  },
  async fetchAllTimeProfitsPerBaseCurrency({ commit }) {
    try {
      const { data } = await axios.get(
        "/api/v1/values/profits-per-base-currency/all-time"
      );

      commit(types.FETCH_ALL_TIME_PROFITS_SUCCESS, {
        profits_per_base_currency: data
      });
    } catch (e) {
      commit(types.FETCH_ALL_TIME_PROFITS_FAILURE);
    }
  }
};



// WEBPACK FOOTER //
// ./resources/js/store/modules/accountValue.js